import React from 'react';
import {
  Box,
  Typography,
  Paper,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@mui/lab';
import { motion } from 'framer-motion';
import { Work as WorkIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Stack from '@mui/material/Stack';

const MotionPaper = motion.create(Paper);

interface ExperienceItem {
  title: string;
  company: string;
  period: string;
  description: string[];
  technologies?: string[];
}

const experiences: ExperienceItem[] = [
  {
    title: 'Founder and Architect',
    company: 'Jobriq (jobriq.com)',
    period: 'November 2025 – Present',
    description: [
      'Architected and built full-stack AI career platform (React, TypeScript, Node.js, Fastify, PostgreSQL, Prisma, Redis/BullMQ) deployed on AWS (EKS, RDS, ElastiCache, S3, CloudFront, Cognito, Bedrock); infrastructure as code via Terraform; CI/CD via GitHub Actions to EKS staging and production',
      'Built multi-agent generative AI pipeline on Amazon Bedrock (Claude Opus/Sonnet/Haiku) for resume analysis, rewriting, ATS compliance, review, cover letters, and interview prep; implemented factual grounding and per-run token/cost tracking',
      'Designed and shipped proprietary viability scoring, iterative ATS-aligned resume optimization, job pipeline tracking, smart nudges/next-best actions, interview prep (16+ questions with profile-grounded coaching), STAR achievement wizard, and profile completeness/coaching',
      'Integrated AWS Cognito (OIDC), Stripe subscriptions (Pro $39/mo) and customer portal, and AWS SES email lifecycle (welcome, nudges, weekly summary, post-interview); launched freemium SaaS Feb 2026',
    ],
    technologies: ['AWS EKS', 'Amazon Bedrock', 'Claude', 'React', 'TypeScript', 'Node.js', 'Fastify', 'PostgreSQL', 'Prisma', 'Redis', 'BullMQ', 'Cognito', 'Stripe', 'Terraform', 'CI/CD', 'GitHub Actions'],
  },
  {
    title: 'Chief Technology Officer',
    company: 'Arizona Regional Multiple Listing Service (ARMLS)',
    period: 'September 2012 - Present',
    description: [
      'Pioneered initiatives focusing on artificial intelligence, machine learning, and Retrieval-Augmented Generation (RAG), leading to transformative technology applications',
      'Elevated ARMLS to one of the top MLSs in the U.S. through strategic IT leadership',
      'Architected and executed a comprehensive digital transformation roadmap, expanding the IT team from 3 to over 40 professionals',
      'Oversee an annual technology budget of $12 million, optimizing resources across cloud computing, data management, and R&D',
      'Championed the development of a public records and listings, achieving significant operational cost savings',
      'Pioneered the creation of Curbview, ARMLS\'s first mobile app, enhancing user engagement and customer service',
    ],
    technologies: ['AWS', 'AI/ML', 'RAG', 'Cloud Architecture', 'Leadership', 'Innovation', 'Digital Transformation'],
  },
  {
    title: 'Principal Consultant',
    company: 'Statera Consulting',
    period: 'October 2005 - September 2012',
    description: [
      'Led multimillion-dollar technical consulting engagements for high-profile clients, delivering measurable business outcomes.',
      'Developed and deployed a machine learning analytics platform for the Arizona Diamondbacks, enabling data-driven decision-making in professional sports.',
      'Engineered automation and robotics solutions for Medtronic, optimizing clean room operations and improving quality control.',
      'Designed and implemented a secure, scalable database application for General Dynamics, supporting mission-critical operations.',
      'Scaled IT infrastructure for Isagenix from $27M to $700M in revenue, leveraging cloud migration and automation.',
      'Architected cloud-native and AI-powered solutions for enterprise clients, accelerating digital transformation and operational efficiency.',
      'Advised clients on responsible AI adoption, data privacy, and security best practices to ensure compliance and trust in next-generation systems.',
    ],
    technologies: ['Machine Learning', 'Cloud Architecture', 'Automation', 'Security', 'Digital Transformation'],
  },
  {
    title: 'Director of Application Development',
    company: 'AIG',
    period: 'July 2004 - October 2005',
    description: [
      'Led development of an audit system tracking millions of stock trades, ensuring compliance with NASD and SEC regulations using terabytes of financial data and symmetric encryption for confidentiality.',
      'Directed business continuity and disaster recovery planning, including full-scale simulation drills and documentation review.',
      'Enhanced application security policies, enabling the organization to pass regulatory compliance audits.',
      'Managed the development of a document imaging system processing over 40,000 documents weekly.',
    ],
    technologies: ['Enterprise Applications', 'Team Leadership'],
  },
  {
    title: 'Director of Technology',
    company: 'Black, Gould & Associates',
    period: 'January 2002 - July 2004',
    description: [
      'Developed an online medical insurance quoting system with HIPAA-compliant security controls for sensitive data.',
      'Enforced programming documentation standards to improve response time to production issues.',
    ],
    technologies: ['Technology Strategy', 'Project Management'],
  },
  {
    title: 'Senior Consultant',
    company: 'Cunningham Consulting',
    period: 'August 1999 - January 2002',
    description: [
      'Implemented virtualization for software development and testing, reducing hardware costs and onboarding time for new developers.',
      'Installed and configured Cisco routers for site-to-site VPN, significantly reducing connectivity costs.',
    ],
    technologies: ['Enterprise Solutions', 'Technical Consulting'],
  },
];

const Experience = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  return (
    <Box sx={{ py: { xs: 2, md: 4 } }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Professional Experience
      </Typography>
      {isMobile ? (
        <Stack spacing={3}>
          {experiences.map((exp, index) => (
            <MotionPaper
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              sx={{
                p: { xs: 2, md: 3 },
                backgroundColor: 'background.paper',
                borderRadius: 2,
              }}
            >
              <Typography variant="h6" component="h2" gutterBottom>
                {exp.title}
              </Typography>
              <Typography variant="subtitle1" color="primary" gutterBottom>
                {exp.company}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {exp.period}
              </Typography>
              <Box component="ul" sx={{ mt: 2, pl: 2 }}>
                {exp.description.map((item, i) => (
                  <Typography
                    component="li"
                    variant="body1"
                    key={i}
                    sx={{ mb: 1 }}
                  >
                    {item}
                  </Typography>
                ))}
              </Box>
              {exp.technologies && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Technologies:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {exp.technologies.map((tech, i) => (
                      <Paper
                        key={i}
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          backgroundColor: 'primary.main',
                          color: 'primary.contrastText',
                          borderRadius: 1,
                        }}
                      >
                        {tech}
                      </Paper>
                    ))}
                  </Box>
                </Box>
              )}
            </MotionPaper>
          ))}
        </Stack>
      ) : (
        <Timeline position="alternate">
          {experiences.map((exp, index) => (
            <TimelineItem key={index}>
              <TimelineSeparator>
                <TimelineDot color="primary">
                  <WorkIcon />
                </TimelineDot>
                {index < experiences.length - 1 && <TimelineConnector />}
              </TimelineSeparator>
              <TimelineContent>
                <MotionPaper
                  initial={{ opacity: 0, x: index % 2 === 0 ? 50 : -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  sx={{
                    p: { xs: 2, md: 3 },
                    backgroundColor: 'background.paper',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h6" component="h2" gutterBottom>
                    {exp.title}
                  </Typography>
                  <Typography variant="subtitle1" color="primary" gutterBottom>
                    {exp.company}
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {exp.period}
                  </Typography>
                  <Box component="ul" sx={{ mt: 2, pl: 2 }}>
                    {exp.description.map((item, i) => (
                      <Typography
                        component="li"
                        variant="body1"
                        key={i}
                        sx={{ mb: 1 }}
                      >
                        {item}
                      </Typography>
                    ))}
                  </Box>
                  {exp.technologies && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Technologies:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {exp.technologies.map((tech, i) => (
                          <Paper
                            key={i}
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              backgroundColor: 'primary.main',
                              color: 'primary.contrastText',
                              borderRadius: 1,
                            }}
                          >
                            {tech}
                          </Paper>
                        ))}
                      </Box>
                    </Box>
                  )}
                </MotionPaper>
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      )}
    </Box>
  );
};

export default Experience; 